/** The door's own settings (/you). Split from the api.ts monolith 2026-08-03; the barrel re-exports. */
import {
  type RawError,
  bareHandle,
  call,
  get,
  nullableNum,
  optTextOf,
  patch,
  post,
  textOf,
} from "./core";
// --- the door's own settings (owner decision 2026-08-03) --------------------

/**
 * The settings an owner writes, and the one they switch.
 *
 * WHAT IS NOT IN THIS TYPE, and must not be added to it: the handle and the
 * display name. X owns both (PRD R10, E-91) — the door is a view of an X
 * identity, and Nob operates no namespace. `refreshFromX` below is the only way
 * either string moves, and it copies rather than edits.
 *
 * NOR IS THERE A PRICE. A door's own hearing charge is gone from the payload
 * and from the PATCH body (economics-wire §6.3): **a door owner has no price
 * control and cannot be given one** (ruling C3). There are no cheap doors and
 * no expensive doors — what varies is how much work a knock asks for, which is
 * a property of the knock and not of the door it arrived at.
 *
 * `daily_knock_cap: null` is UNCAPPED — the absence of a cap, not an unknown one.
 *
 * `bio` IS here, and it is the one thing on this screen the owner writes about
 * THEMSELVES (FR-3.8.1) rather than about the door. `null` is a door with no
 * bio, which renders without one — the public page invents nothing to fill it.
 */
export type DoorSettings = {
  status: "live" | "paused";
  persona_name: string;
  persona_tone: string;
  daily_knock_cap: number | null;
  bio: string | null;
  /**
   * WHAT GETS THROUGH, AS STRANGERS READ IT (ruling R-0910-10) — read from this
   * payload, written by `updateDoorPublicLine` below and by nothing else. It is
   * not on `DoorSettingsInput` and must not be added to it: the line is screened
   * on its own route, and a second road onto the public page would be a road
   * with no screen on it.
   */
  public_line: string | null;
};

/**
 * Only the keys present are written. `daily_knock_cap: null` means uncapped,
 * and `bio: null` means clear it — both are instructions, not absences.
 */
export type DoorSettingsInput = {
  status?: "live" | "paused";
  persona_name?: string;
  persona_tone?: string;
  daily_knock_cap?: number | null;
  /**
   * Taking a LIVE door down is the one setting change the server makes somebody
   * type out: the handle, in capitals. Sent only with `status: "paused"`, and
   * never invented here — it is what the person typed, and a wrong or missing
   * one is refused (400 CONFIRM_REQUIRED). Putting the door back up carries
   * nothing: reopening is not a thing anybody does by accident.
   */
  confirm_handle?: string;
  /** The owner's own sentence for the door page; null clears it (FR-3.8.1). */
  bio?: string | null;
};

/** A server refusal, kept attached to the field that earned it. */
export type FieldIssue = { field: string; message: string };

export type DoorSettingsResult =
  | { ok: true; settings: DoorSettings }
  | {
      ok: false;
      code:
        | "UNAUTHENTICATED"
        | "DOOR_NOT_FOUND"
        | "VALIDATION_ERROR"
        /** The takedown was not confirmed by typing the handle. The door is untouched. */
        | "CONFIRM_REQUIRED"
        | "BIO_TOO_LARGE"
        | "INPUT_REFUSED"
        | "UNKNOWN";
      /** The server's own sentences, for rendering beside the offending inputs. */
      issues: FieldIssue[];
    };

/**
 * The refusals, IN THE SERVER'S WORDS. The API states its numbers — the floor,
 * the ceiling, the character limits — and this client neither rewrites them nor
 * invents a friendlier version: a form that paraphrases a rule it does not own
 * eventually paraphrases it wrong, and the person is left arguing with a
 * sentence nothing enforces.
 */
function fieldIssues(body: unknown): FieldIssue[] {
  const details = (body as RawError).error?.details;
  const raw =
    details && typeof details === "object" ? (details as { issues?: unknown }).issues : [];
  if (!Array.isArray(raw)) return [];
  const issues: FieldIssue[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const i = item as { path?: unknown; message?: unknown };
    const field = Array.isArray(i.path)
      ? i.path.filter((p): p is string => typeof p === "string").join(".")
      : "";
    const message = typeof i.message === "string" ? i.message.trim() : "";
    if (message) issues.push({ field, message });
  }
  return issues;
}

function settingsFailure(status: number, body: unknown): DoorSettingsResult {
  const issues = fieldIssues(body);
  if (status === 401) return { ok: false, code: "UNAUTHENTICATED", issues };
  if (status === 404) return { ok: false, code: "DOOR_NOT_FOUND", issues };
  if (status === 400) {
    // The ceremony's own refusal, kept apart from ordinary validation: it is
    // not a field that was mistyped, it is a confirmation that was not given,
    // and the modal it belongs to has to stay open to ask again.
    const code = (body as RawError).error?.code;
    if (code === "CONFIRM_REQUIRED") return { ok: false, code: "CONFIRM_REQUIRED", issues };
    return { ok: false, code: "VALIDATION_ERROR", issues };
  }
  // The bio's two content refusals (FR-3.8.1) arrive as CODES, not as field
  // sentences: the server is refusing what the text says, not what shape it is.
  // The room supplies the sentence (you-page.tsx).
  if (status === 422) {
    const code = (body as RawError).error?.code;
    if (code === "BIO_TOO_LARGE" || code === "INPUT_REFUSED") {
      return { ok: false, code, issues };
    }
  }
  return { ok: false, code: "UNKNOWN", issues };
}

/**
 * Normalised the way everything else here is, with one exception that matters:
 * `daily_knock_cap` is read as null unless it is a real number. `num()` would
 * floor a missing cap to 0, and 0 is not "no cap" — it is a door nobody may
 * knock at, which is a different setting entirely and one this product spells
 * `paused`.
 */
function normaliseSettings(raw: unknown): DoorSettings {
  const b = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    status: b["status"] === "paused" ? "paused" : "live",
    persona_name: textOf(b["persona_name"]),
    persona_tone: textOf(b["persona_tone"]),
    daily_knock_cap: nullableNum(b["daily_knock_cap"]),
    // A blank bio and no bio are the same thing, and the form renders both as
    // an empty box rather than as whitespace somebody has to notice and delete.
    bio: optTextOf(b["bio"]),
    public_line: optTextOf(b["public_line"]),
  };
}

export async function fetchDoorSettings(): Promise<DoorSettingsResult> {
  const r = await call(() => get("/api/v1/door/settings"));
  if (!r) return { ok: false, code: "UNKNOWN", issues: [] };
  if (!r.res.ok) return settingsFailure(r.res.status, r.body);
  return { ok: true, settings: normaliseSettings(r.body) };
}

/** Only the keys that moved are written; a price is not one of them any more. */
export async function updateDoorSettings(input: DoorSettingsInput): Promise<DoorSettingsResult> {
  const body: Record<string, unknown> = {};
  if (input.status) body["status"] = input.status;
  if (typeof input.persona_name === "string") body["persona_name"] = input.persona_name;
  if (typeof input.persona_tone === "string") body["persona_tone"] = input.persona_tone;
  // `in`, not a truthiness check: an explicit null is the instruction "uncapped",
  // and it has to survive the trip to the wire as a null.
  if ("daily_knock_cap" in input) body["daily_knock_cap"] = input.daily_knock_cap ?? null;
  // Rides only with the change it confirms; the server refuses a takedown
  // without it, and a reopening carries none.
  if (typeof input.confirm_handle === "string") body["confirm_handle"] = input.confirm_handle;
  // Same reasoning as the cap: `bio: null` clears it (FR-3.8.1), and a
  // truthiness check would silently turn "clear this" into "leave it alone".
  if ("bio" in input) body["bio"] = input.bio ?? null;

  const r = await call(() => patch("/api/v1/door/settings", body));
  if (!r) return { ok: false, code: "UNKNOWN", issues: [] };
  if (!r.res.ok) return settingsFailure(r.res.status, r.body);
  return { ok: true, settings: normaliseSettings(r.body) };
}

/**
 * THE DOOR'S PUBLIC LINE (ruling R-0910-10) — its own PATCH, because it is its
 * own screen.
 *
 * `null` clears it, and a cleared line means the plate carries no such block at
 * all. The refusals are the bio's, in the same two codes, because the two
 * fields are refused for the same two reasons and the room already prints a
 * sentence for each.
 */
export type PublicLineResult =
  | { ok: true; public_line: string | null }
  | {
      ok: false;
      code:
        | "UNAUTHENTICATED"
        | "DOOR_NOT_FOUND"
        | "VALIDATION_ERROR"
        | "BIO_TOO_LARGE"
        | "INPUT_REFUSED"
        | "UNKNOWN";
    };

export async function updateDoorPublicLine(line: string | null): Promise<PublicLineResult> {
  const r = await call(() => patch("/api/v1/door/public-line", { public_line: line }));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) {
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
    if (r.res.status === 404) return { ok: false, code: "DOOR_NOT_FOUND" };
    const code = (r.body as RawError).error?.code;
    if (code === "BIO_TOO_LARGE" || code === "INPUT_REFUSED" || code === "VALIDATION_ERROR") {
      return { ok: false, code };
    }
    return { ok: false, code: "UNKNOWN" };
  }
  return { ok: true, public_line: optTextOf((r.body as Record<string, unknown>)["public_line"]) };
}

/**
 * "Ask Nob to write it from your rules" (ruling R-0910-10 rule 4).
 *
 * A DRAFT, NOT A SAVE: the line goes into the field the owner is looking at,
 * and `updateDoorPublicLine` above is still the only write. It is AI work on
 * the owner's own lane, so it is refused for money exactly as a doorman turn is
 * — on Free that refusal is the expected answer (ruling C10), and the room
 * disables the button before it comes to that.
 */
export type PublicLineDraftResult =
  | { ok: true; public_line: string }
  | {
      ok: false;
      code:
        | "UNAUTHENTICATED"
        | "DOOR_NOT_FOUND"
        /** The door has no rules of its own to draw a line from. */
        | "NO_RULES"
        | "PROVIDER_UNAVAILABLE"
        /** The drafted line would not have cleared the save's screen. */
        | "INPUT_REFUSED"
        /** The two money refusals a metered call can meet (ToU 2.2 §5.3, §8.9). */
        | "PLAN_EXHAUSTED"
        | "PAYMENT_RISK_SUSPENDED"
        | "UNKNOWN";
    };

export async function draftPublicLine(): Promise<PublicLineDraftResult> {
  const r = await call(() => post("/api/v1/door/public-line/draft", {}));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) {
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
    const code = (r.body as RawError).error?.code;
    switch (code) {
      case "DOOR_NOT_FOUND":
      case "NO_RULES":
      case "PROVIDER_UNAVAILABLE":
      case "INPUT_REFUSED":
      case "PLAN_EXHAUSTED":
      case "PAYMENT_RISK_SUSPENDED":
        return { ok: false, code };
      default:
        return { ok: false, code: "UNKNOWN" };
    }
  }
  const line = textOf((r.body as Record<string, unknown>)["public_line"]);
  // An answer with no line in it is not an answer — the whole point of the call
  // is the string, and an empty field is not something to write into a form.
  if (!line) return { ok: false, code: "PROVIDER_UNAVAILABLE" };
  return { ok: true, public_line: line };
}

/**
 * Re-read this person's identity from X (E-91). Not an edit — a copy: the
 * handle and the display name belong to X, and the profile room says so beside
 * the button rather than offering fields that would imply otherwise.
 */
export type RefreshedIdentity = { handle: string; display_name: string };

export type RefreshResult =
  | { ok: true; identity: RefreshedIdentity }
  | {
      ok: false;
      code: "UNAUTHENTICATED" | "PROVIDER_UNAVAILABLE" | "UNKNOWN";
    };

export async function refreshFromX(): Promise<RefreshResult> {
  const r = await call(() => post("/api/v1/me/refresh", {}));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) {
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
    const code = (r.body as RawError).error?.code;
    if (code === "PROVIDER_UNAVAILABLE") return { ok: false, code: "PROVIDER_UNAVAILABLE" };
    return { ok: false, code: "UNKNOWN" };
  }
  const b = r.body as Record<string, unknown>;
  const handle = bareHandle(textOf(b["handle"]));
  // An answer with no address in it is not an answer: the whole point of the
  // call is the string it comes back with.
  if (!handle) return { ok: false, code: "UNKNOWN" };
  return { ok: true, identity: { handle, display_name: textOf(b["display_name"]) } };
}
